import { useQuery } from '@tanstack/react-query';
import { useMedplum, useMedplumProfile } from '@medplum/react';

import {
	APP_PERMISSIONS,
	permissionsQueryKey,
	hasPermission as checkPermission,
} from '@/lib/auth/permissions';
import type { AppPermission, PermissionsResponse } from '@/lib/auth/permissions';

import { useBot } from './useBot';

export function usePermissions() {
	const medplum = useMedplum();
	const profile = useMedplumProfile();
	const { data: bot, isLoading: isBotLoading } = useBot('get-permissions');

	const practitionerId = profile?.resourceType === 'Practitioner' ? profile.id : undefined;

	const query = useQuery({
		queryKey: [...permissionsQueryKey(practitionerId), bot?.id],
		queryFn: async (): Promise<PermissionsResponse> => {
			if (!bot?.id) {
				throw new Error('Get Permissions Bot not found');
			}

			return medplum.executeBot(bot.id, {
				practitionerId,
			}) as Promise<PermissionsResponse>;
		},
		enabled: !!practitionerId && !!bot,
		staleTime: Number.POSITIVE_INFINITY,
	});

	const permissions = query.data?.permissions ?? [];
	const role = query.data?.role ?? '';
	const isSuperAdmin = permissions.includes(APP_PERMISSIONS.SUPERADMIN);

	return {
		permissions,
		role,
		hasPermission: (p: AppPermission) => checkPermission(permissions, p),
		isSuperAdmin,
		hasAdminAccess:
			isSuperAdmin ||
			checkPermission(permissions, APP_PERMISSIONS.MANAGE_USERS) ||
			checkPermission(permissions, APP_PERMISSIONS.MANAGE_ENTITIES),
		isLoading: isBotLoading || query.isLoading,
	};
}
